/**
 * apps/web :: pass :: screens/TripLifecycleHub
 *
 * Trip-lifecycle navigator (ROADS-569 W1.C3). Three phase cards — before,
 * during and after the trip — each linking to its checklist, plus a
 * courseware entry. Resolves the traveler's first active `tourist_out`
 * country (fallback "IT") only for PassLayout chrome; the phase content is
 * country-agnostic at this level.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { Link } from "react-router-dom";

import PassLayout from "../PassLayout";
import { useActivePassCountries } from "@/lib/pass-content";

interface Phase {
  key: string;
  to: string;
  icon: string;
  title: string;
  description: string;
  /** Tailwind classes for the card's icon chip. */
  chip: string;
}

const PHASES: Phase[] = [
  {
    key: "pre",
    to: "../pre-trip",
    icon: "🧳",
    title: "Before you leave home",
    description: "Permits, insurance, apps and the offline pack — sorted early.",
    chip: "bg-emerald-100 text-emerald-700",
  },
  {
    key: "on",
    to: "../on-trip",
    icon: "🚗",
    title: "While you're driving",
    description: "Zones, parking and the documents to keep within reach.",
    chip: "bg-indigo-100 text-indigo-700",
  },
  {
    key: "post",
    to: "../post-trip",
    icon: "📬",
    title: "After you're home",
    description: "Check official portals and pay inside the discount window.",
    chip: "bg-amber-100 text-amber-700",
  },
];

export default function TripLifecycleHub(): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";

  return (
    <PassLayout country={country} title="Plan your trip">
      <p className="text-sm text-neutral-700">
        Three phases, one calm trip. Work through each checklist as it comes up.
      </p>

      <ul className="mt-lg flex flex-col gap-3">
        {PHASES.map((phase, index) => (
          <li key={phase.key}>
            <Link
              to={phase.to}
              data-testid={`trip-phase-${phase.key}`}
              className="flex items-start gap-3 rounded-lg border border-neutral-200 px-md py-md transition hover:border-indigo-300 hover:bg-indigo-50/40"
            >
              <span
                aria-hidden="true"
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg ${phase.chip}`}
              >
                {phase.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold uppercase tracking-wide text-neutral-400">
                  Phase {index + 1}
                </span>
                <span className="mt-0.5 block text-sm font-semibold text-neutral-900">
                  {phase.title}
                </span>
                <span className="mt-0.5 block text-sm text-neutral-600">
                  {phase.description}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <Link
        to="../learn"
        data-testid="trip-learn-entry"
        className="mt-lg flex items-center justify-between rounded-lg bg-indigo-600 px-md py-md text-white transition hover:bg-indigo-700"
      >
        <span>
          <span className="block text-sm font-semibold">Driving Abroad course</span>
          <span className="mt-0.5 block text-xs text-indigo-100">
            Short lessons that tie the whole trip together.
          </span>
        </span>
        <span aria-hidden="true" className="text-lg">
          →
        </span>
      </Link>
    </PassLayout>
  );
}
